import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

const NAVY = '#173B67';
const TEAL = '#43A6A1';
const ON_DARK = '#EAF3FA';

export type BrandLockupLayout = 'horizontal' | 'stacked' | 'icon';

export interface BrandLockupProps {
  height?: number;
  layout?: BrandLockupLayout;
  onDark?: boolean;
  suffix?: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export const BrandLockup: React.FC<BrandLockupProps> = ({
  height = 40,
  layout = 'horizontal',
  onDark = false,
  suffix,
  onClick,
  sx,
}) => {
  const iconOnly = layout === 'icon';
  const iconSrc = onDark ? '/logos/icon-on-dark.png' : '/logos/icon.png';
  const fontSize = Math.max(16, Math.round(height * (layout === 'stacked' ? 0.4 : 0.48)));
  const shareColor = onDark ? ON_DARK : NAVY;

  const wordmark = (
    <Box
      component="span"
      sx={{
        fontWeight: 700,
        fontSize,
        lineHeight: 1,
        letterSpacing: '-0.03em',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      <Box component="span" sx={{ color: shareColor }}>
        Share
      </Box>
      <Box component="span" sx={{ color: TEAL }}>
        Giver
      </Box>
      {suffix ? (
        <Box
          component="span"
          sx={{
            color: shareColor,
            fontWeight: 600,
            ml: 0.75,
          }}
        >
          {suffix}
        </Box>
      ) : null}
    </Box>
  );

  const lockupSx = {
    display: 'inline-flex',
    flexDirection: layout === 'stacked' ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: layout === 'stacked' ? 0.75 : 1,
    background: 'none',
    border: 0,
    padding: 0,
    margin: 0,
    cursor: onClick ? 'pointer' : 'default',
    color: 'inherit',
    font: 'inherit',
    '&:hover': onClick ? { opacity: 0.85 } : undefined,
  } as const;

  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-label={iconOnly ? 'ShareGiver' : undefined}
      sx={(sx ? [lockupSx, sx] : lockupSx) as SxProps<Theme>}
    >
      <Box
        component="img"
        src={iconSrc}
        alt={iconOnly ? 'ShareGiver' : ''}
        sx={{
          height,
          width: 'auto',
          display: 'block',
        }}
      />
      {iconOnly ? null : wordmark}
    </Box>
  );
};
